import {
  type ColumnType,
  Table,
  type TablePaginationConfig,
} from '@/components';
import { MenuOutlined } from '@ant-design/icons';
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { ExpandableConfig } from 'antd/es/table/interface';
import type { RowProps } from 'antd/lib';
import {
  type CSSProperties,
  Children,
  type PropsWithChildren,
  type ReactElement,
  cloneElement,
  isValidElement,
} from 'react';

type Props<T> = {
  dataSource: T;
  columns: Array<ColumnType<any>>;
  rowKey: string;
  rowKeys: string[];
  pagination?: TablePaginationConfig | false;
  onDragEnd: ({ active, over }: DragEndEvent) => void;
  enableSticky?: boolean;
  expandable?: ExpandableConfig<any>;
};

// NOTE:
// Only all data sources handled by this component.
// Paged or filtered data sources cannot be handled.
export const SortableTable = <T extends any[]>({
  dataSource,
  columns,
  rowKey,
  rowKeys,
  pagination = false,
  enableSticky = false,
  onDragEnd,
  expandable,
}: Props<T>) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        // https://docs.dndkit.com/api-documentation/sensors/pointer#activation-constraints
        distance: 1,
      },
    }),
  );

  return (
    <DndContext sensors={sensors} onDragEnd={onDragEnd}>
      <SortableContext items={rowKeys}>
        <Table
          enableSticky={enableSticky}
          rowKey={rowKey}
          components={{
            body: {
              row: Row,
            },
          }}
          columns={[
            {
              key: 'sort',
              width: 35,
            },
            ...columns,
          ]}
          dataSource={dataSource}
          pagination={pagination}
          expandable={expandable}
        />
      </SortableContext>
    </DndContext>
  );
};

const Row = ({ children, ...props }: RowProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: props['data-row-key'],
  });

  const style: CSSProperties = {
    ...props.style,
    transform: CSS.Transform.toString(transform && { ...transform, scaleY: 1 }),
    transition,
    ...(isDragging
      ? { position: 'relative', zIndex: 9999, cursor: 'grabbing' }
      : {}),
  };

  // ref. https://ant.design/components/table#components-table-demo-drag-sorting-handler
  return (
    <tr {...props} ref={setNodeRef} style={style} {...attributes}>
      {Children.map(children, (child) => {
        if (
          isValidElement<PropsWithChildren>(child) &&
          (child as ReactElement).key === 'sort'
        ) {
          return cloneElement(child, {
            children: (
              <MenuOutlined
                ref={setActivatorNodeRef}
                style={{
                  touchAction: 'none',
                  cursor: isDragging ? 'grabbing' : 'grab',
                }}
                {...listeners}
              />
            ),
          });
        }
        return child;
      })}
    </tr>
  );
};
